import React from 'react';
import { Head, Link } from '@inertiajs/react';
import DashboardLayout from '../../../components/DashboardLayout';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Mail, Calendar, Users, Plus, Eye, FileText } from 'lucide-react';

interface NewsletterData {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  access_type: 'open_access' | 'restricted_access';
  subscription_type: 'open_subscription' | 'auth_required';
  delivery_schedule: 'manual' | 'daily' | 'weekly' | 'monthly';
  is_active: boolean;
  subscriber_count: number;
  created_at: string;
}

interface IndexProps {
  newsletters: NewsletterData[];
}

const Index: React.FC<IndexProps> = ({ newsletters }) => {
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getAccessBadgeClass = (accessType: string): string => {
    switch (accessType) {
      case 'open_access':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      default:
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300';
    }
  };

  const getSubscriptionBadgeClass = (subType: string): string => {
    switch (subType) {
      case 'open_subscription':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
      default:
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300';
    }
  };

  const getScheduleLabel = (schedule: string): string => {
    switch (schedule) {
      case 'daily':
        return 'Daily';
      case 'weekly':
        return 'Weekly';
      case 'monthly':
        return 'Monthly';
      default:
        return 'Manual';
    }
  };

  return (
    <DashboardLayout>
      <Head title="Newsletter Management" />

      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Newsletters</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Manage your newsletter campaigns and subscribers.
            </p>
          </div>
          <Link href="/admin/newsletters/new">
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Create Newsletter
            </Button>
          </Link>
        </div>

        {/* Newsletters Grid */}
        {newsletters.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {newsletters.map((newsletter) => (
              <Card key={newsletter.id} className="rounded-xl border bg-card text-card-foreground shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-lg font-semibold">
                      {newsletter.name}
                    </CardTitle>
                    {newsletter.is_active ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300">
                        Inactive
                      </span>
                    )}
                  </div>
                  {newsletter.description && (
                    <CardDescription className="mt-1">
                      {newsletter.description}
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Badges */}
                  <div className="flex flex-wrap gap-2">
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getAccessBadgeClass(
                        newsletter.access_type
                      )}`}
                    >
                      {newsletter.access_type === 'open_access' ? 'Public' : 'Restricted'}
                    </span>
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getSubscriptionBadgeClass(
                        newsletter.subscription_type
                      )}`}
                    >
                      {newsletter.subscription_type === 'open_subscription' ? 'Open Subscribe' : 'Auth Required'}
                    </span>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300">
                      {getScheduleLabel(newsletter.delivery_schedule)}
                    </span>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      <span>{newsletter.subscriber_count} subscribers</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      <span>{formatDate(newsletter.created_at)}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 pt-2">
                    <Link
                      href={`/admin/newsletters/${newsletter.id}`}
                      className="flex-1"
                    >
                      <Button variant="outline" className="w-full">
                        <Eye className="h-4 w-4 mr-2" />
                        View
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="rounded-xl border bg-card text-card-foreground shadow">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <FileText className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-1">No newsletters yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Create your first newsletter to start building your audience.
              </p>
              <Link href="/admin/newsletters/new">
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Newsletter
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Index;
